import { Box, Button, Container, chakra, Heading, Stack, Text } from '@chakra-ui/react'
import React from 'react'

import logger from '@/utils/logger'
import { makeServerProps } from '@/server/query'

import makePage from '@/client/services/pages/makePage'
import { email, getBlogCategoryUrl, getPublicUrl, getRoadmapUrl } from '@/client/services/url'
import { getArticleUrl } from '@/client/services/url/faq'

import Hero from '@/components/Landing/Hero'
import Section from '@/components/Landing/Section'
import Layout from '@/components/Layout/Layout'
import ShwLink from '@/components/ShwLink'
import Spinner from '@/components/Spinner'
import Card from '@/components/Worlds/UI/Card'

type CareerPostings = { jobs: { title: string; jobUrl: string }[] }

const ViewAllRolesButton = () => (
  <ShwLink href="https://jobs.ashbyhq.com/schoolhouse-world">
    <Button width="fit-content">View All Roles</Button>
  </ShwLink>
)

const Careers: React.FC = () => {
  const [careerPostings, setCareerPostings] = React.useState<CareerPostings | null>(null)

  React.useEffect(() => {
    fetch(
      'https://api.ashbyhq.com/posting-api/job-board/schoolhouse-world?includeCompensation=true'
    )
      .then(res => res.json())
      .then((data: CareerPostings) => {
        setCareerPostings({
          jobs: [...data.jobs].sort((jobA, jobB) => jobA.title.localeCompare(jobB.title)),
        })
      })
      .catch(err => {
        logger.error('Error loading job postings', { err })
      })
  }, [])

  return (
    <Layout title="Careers">
      <Hero
        title="Build the Future of Education"
        description={() => (
          <Text maxWidth="768px" textAlign="center">
            We&apos;re on a mission to build an inclusive and equitable platform for learners across
            the world. Join us on our journey to shape the future of education!
          </Text>
        )}
        includeKAFounder
        primaryCTA={ViewAllRolesButton}
        bg="bg.info"
      />
      <Hero
        title={() => (
          <Heading size="2xl">
            We are <chakra.span color="fg.muted">15</chakra.span> people working together across
            borders
          </Heading>
        )}
        showGraphicOnMobile
        description={() => (
          <Stack direction="column" spacing="16px" maxWidth="500px">
            <Text>
              We&apos;re a tight-knit, ambitious, and passionate remote team—small but mighty—and
              thrive in a fast-paced environment. Every day, we tackle some of the most interesting
              problems in learning and education.
            </Text>
            <Text>
              To learn more, check out our{' '}
              <ShwLink href={getBlogCategoryUrl('hq')} isExternal>
                blog
              </ShwLink>
              , <ShwLink href={getRoadmapUrl()}>public roadmap</ShwLink>, and{' '}
              <ShwLink href={getArticleUrl('community-values')}>organizational values</ShwLink>.
            </Text>
          </Stack>
        )}
        primaryCTA={undefined}
        bg="bg"
        graphic={{
          src: '/careers/team-02-15-2024-v2.png',
          alt: 'The Schoolhouse team',
          borderRadius: '4px',
        }}
      />
      <Section bg="bg.subtle" py="32px">
        <Heading size="2xl" as="h2" mb="32px" id="open-positions">
          Open Positions
        </Heading>
        {careerPostings == null ? (
          <Spinner />
        ) : careerPostings.jobs.length > 0 ? (
          <Stack direction="column" spacing="16px">
            {careerPostings.jobs.map(({ title, jobUrl }) => (
              <ShwLink href={jobUrl} key={title}>
                <Card key={title} p="16px">
                  <Heading size="md" color="primary.fg">
                    {title}
                  </Heading>
                </Card>
              </ShwLink>
            ))}
          </Stack>
        ) : (
          <Text>We have no open roles at the moment. Please check back again soon!</Text>
        )}
      </Section>
      <Box bg="bg.subtle">
        <Container py="0px !important" px={{ base: '0px', sm: '24px' }}>
          <Stack
            bg="bg.secondary"
            direction="column"
            align="center"
            borderTopRadius={{ base: '0px', sm: '16px' }}
            py="64px"
            px="24px"
            textAlign="center"
            backgroundImage={{
              base: 'none',
              lg: getPublicUrl('careers/contact-us-background.svg'),
            }}
          >
            <Heading size="2xl" mb="24px">
              No open role that fits you, but still want to join?
            </Heading>
            <Text maxWidth="600px" textAlign="center">
              Excited about what we&apos;re building but none of our positions resonate with you?
              We&apos;d love to hear from you at{' '}
              <ShwLink href={email.get('careers')}>{email.get('careers', { as: 'text' })}</ShwLink>.
            </Text>
          </Stack>
        </Container>
      </Box>
    </Layout>
  )
}

const CareersPage = makePage(Careers, {
  onboardingUsers: 'allowed',
})
export const getServerSideProps = makeServerProps(CareersPage)
export default CareersPage
